import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import {
  Image,
  X,
  Plus,
  ExternalLink,
  Sparkles,
  AlertCircle,
  Shuffle,
  Megaphone,
} from 'lucide-react';
import { AppInput } from '@/shared/components/ui/AppInput/AppInput';
import { AppButton } from '@/shared/components/ui/AppButton/AppButton';
import { AppSelect } from '@/shared/components/ui/AppSelect/AppSelect';
import { FEATURE_FLAGS } from '@/shared/config/feature-flags';
import { useFeatureFlag } from '@/shared/hooks/use-feature-flag';
import { useUIStore } from '@/shared/stores/ui-store';
import { useAuthStore } from '@/shared/stores/auth-store';
import { CreativeType, AdPlatform, CampaignObjective } from '@/shared/types/enums';
import {
  CreativeUploader,
  type UploadFileItem,
} from '@/shared/components/promote/CreativeUploader/CreativeUploader';
import {
  PLATFORM_FILE_CONSTRAINTS,
  validateVideoDuration,
  validateImageDimensions,
} from '@/shared/components/promote/CreativeUploader/CreativeUploader.logic';
import {
  creativeSchema,
  boostCreativeSchema,
  type CreativeFormValues,
} from '../../../schemas/creative.schema';
import {
  CTA_OPTIONS,
  CREATIVE_TYPE_OPTIONS,
  type WizardCreative,
} from '../../../types/campaigns.types';
import { useCampaignWizardStore } from '../../../stores/campaign-wizard-store';
import { useWizardInvalidToast } from '../../../hooks/use-wizard-invalid-toast/use-wizard-invalid-toast';
import {
  useCopySuggestion,
  useUploadCreativeImage,
  useUploadCreativeVideo,
} from '../../../api/campaigns-mutations';
import { fetchAssetAccessUrl } from '../../../api/campaigns-api';
import {
  PLATFORM_CREATIVE_CONSTRAINTS,
  PLATFORM_CREATIVE_HELP,
  validateCreativeMedia,
  validateFieldCount,
  CAROUSEL_MAX_CARDS,
} from '../../../logic/platform-constraints';
import type { PlatformCreativeConstraints } from '../../../logic/platform-constraints';
import { scrollToFirstError } from '../../../logic/scroll-to-first-error';
import { MetaPostPicker } from './MetaPostPicker/MetaPostPicker';
import { AdCopyFields } from './AdCopyFields';
import { ensureAtLeastOne } from './AdCopyFields/AdCopyFields.logic';
import styles from './CreativeStep.module.scss';

interface CreativeStepProps {
  onNext: () => void;
  onBack: () => void;
  onSaveDraft: () => void;
  isSaving: boolean;
}

const DEFAULT_CREATIVE: WizardCreative = {
  type: CreativeType.IMAGE,
  headlines: [''],
  descriptions: [''],
  callToAction: 'BUY_TICKETS',
  mediaFiles: [],
  mediaPreviews: [],
  generatedImages: [],
  squareMediaFiles: [],
  squareMediaPreviews: [],
  squareGeneratedImages: [],
};

function getConstraints(platform: AdPlatform | undefined): PlatformCreativeConstraints | null {
  if (!platform) return null;
  return PLATFORM_CREATIVE_CONSTRAINTS[platform] ?? null;
}

/** Real upload status of a manually-added file, keyed by fingerprint. */
type FileUploadState = 'uploading' | 'done' | 'error';

/**
 * Image slot. Most campaigns use only 'landscape' (the single image). Google
 * Display also needs a 'square' (1:1) image alongside the landscape (1.91:1).
 */
type ImageSlot = 'landscape' | 'square';

/** Stable identity for a browser-picked File across re-renders. */
function fileFingerprint(file: File): string {
  return `${file?.name}::${file?.size}::${file?.lastModified}`;
}

/**
 * Builds UploadFileItem[] from the existing form data (mediaFiles + mediaPreviews)
 * so the CreativeUploader can display previously added files. The progress bar
 * reflects the REAL S3 upload status — not a cosmetic 100% — so a file whose
 * upload is still in flight or failed never looks complete.
 */
function buildUploadFileItems(
  mediaFiles: File[],
  mediaPreviews: string[],
  uploadStates: Record<string, FileUploadState>,
  uploadErrors: Record<string, string>,
): UploadFileItem[] {
  return mediaFiles.map((file, index) => {
    // No tracked state means the file came from a rehydrated draft (already
    // persisted) — treat it as complete. Otherwise mirror the real upload.
    const fp = fileFingerprint(file);
    const state = uploadStates[fp];
    const progress = state === 'uploading' ? 35 : state === 'error' ? -1 : 100;
    return {
      id: `existing-${index}-${file.name}`,
      file,
      previewUrl: mediaPreviews[index] ?? '',
      progress,
      // Show the specific rejection reason (e.g. image dimensions) when we
      // have one — the generic copy hides why the file was refused.
      error:
        state === 'error'
          ? (uploadErrors[fp] ?? 'Upload failed — remove the file and add it again.')
          : undefined,
    };
  });
}

/**
 * Wizard Step 4: Create ad creatives (headline, media, CTA).
 */
export function CreativeStep({ onNext, onBack, onSaveDraft, isSaving }: CreativeStepProps) {
  const { formData, updateFormData, markStepComplete } = useCampaignWizardStore();
  const addToast = useUIStore((s) => s.addToast);
  // The wizard edits a single pooled creative: one set of images + headlines +
  // descriptions that the platform mixes freely (Google RDA, Meta asset feeds).
  // Field paths keep the array shape (`creatives.0.*`) so the store, schema and
  // payload mapper stay untouched.
  const activeCreativeIndex = 0;
  const copySuggestion = useCopySuggestion();
  const generateCopyEnabled = useFeatureFlag(FEATURE_FLAGS.AI_GENERATE_COPY);
  const [platformErrors, setPlatformErrors] = useState<string[]>([]);
  // Real per-file upload status (the green bar must not lie). Files added before
  // the session's orgId is ready are parked here and fired once it arrives.
  const [uploadStates, setUploadStates] = useState<Record<string, FileUploadState>>({});
  // Specific rejection reason per file (dimensions, upload failure), keyed by
  // fingerprint — surfaced on the file item so the user sees WHY it failed.
  const [uploadErrors, setUploadErrors] = useState<Record<string, string>>({});
  const orgIdQueueRef = useRef<{ file: File; index: number; slot: ImageSlot }[]>([]);

  const constraints = getConstraints(formData.platform);
  const platformHelpText = formData.platform
    ? PLATFORM_CREATIVE_HELP[formData.platform]
    : undefined;

  // Ensure both pools have at least one entry, here. A restored draft can
  // arrive with an empty pool. `defaultValues` is the right place because the
  // step remounts on every wizard navigation (AnimatePresence keys on the step).
  // Doing it in an effect instead would loop against the debounced store
  // writeback below.
  const defaultCreatives = useMemo(() => {
    const base =
      formData.creatives && formData.creatives.length > 0
        ? formData.creatives.slice(0, 1)
        : [DEFAULT_CREATIVE];
    return base.map((creative) => ({
      ...creative,
      headlines: ensureAtLeastOne(creative.headlines ?? ['']),
      descriptions: ensureAtLeastOne(creative.descriptions ?? ['']),
    }));
    // Intentionally computed once per mount — RHF reads defaultValues only once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Boosting is only a Meta concept, and only once a Page is chosen — the post
  // belongs to that Page. Resuming a draft that was boosting restores the mode.
  const canBoost = formData.platform === AdPlatform.META && !!formData.pageId;
  const [isBoosting, setIsBoosting] = useState(canBoost && !!formData.boostPostId);

  // Swapping the resolver (rather than branching inside one schema) keeps the
  // creative rules untouched: in boost mode there is no headline or image to
  // validate, and the fields the organizer already filled stay in form state so
  // switching back restores them.
  const resolver = useMemo(
    () => zodResolver(isBoosting ? boostCreativeSchema : creativeSchema),
    [isBoosting],
  );

  const form = useForm<CreativeFormValues>({
    resolver,
    defaultValues: {
      creatives: defaultCreatives,
      destinationUrl: formData.destinationUrl ?? '',
      boostPostId: formData.boostPostId ?? '',
    },
  });

  const {
    control,
    handleSubmit,
    register,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = form;

  const onInvalidSubmit = useWizardInvalidToast();

  const watchedCreatives = watch('creatives');
  const destinationUrl = watch('destinationUrl');
  const activeCreative = watchedCreatives[activeCreativeIndex];

  // Keep the persistent Ad Preview (wizard right rail) live as the user edits,
  // not just on Continue. RHF's watch(callback) fires on field changes (not on
  // every render), so writing to the store here can't loop with re-renders.
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const subscription = watch(() => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        const values = getValues();
        updateFormData({
          creatives: values.creatives.map((creative) => ({
            ...creative,
            mediaFiles: creative.mediaFiles ?? [],
            mediaPreviews: creative.mediaPreviews ?? [],
            generatedImages: creative.generatedImages ?? [],
          })),
          destinationUrl: values.destinationUrl,
        });
      }, 250);
    });
    return () => {
      subscription.unsubscribe();
      if (timeout) clearTimeout(timeout);
    };
  }, [watch, getValues, updateFormData]);

  // Build preview items for the Ad Preview section (generated images only)
  const generatedPreviewItems = useMemo(
    () =>
      (activeCreative?.generatedImages ?? []).map((image, index) => ({
        key: `generated-${image.imageStorageKey}-${index}`,
        url: image.imageUrl,
        source: 'generated' as const,
        index,
      })),
    [activeCreative?.generatedImages],
  );

  // Google AWARENESS maps to a Display campaign, which needs BOTH a landscape
  // (1.91:1) and a square (1:1) image → show two uploaders for that case.
  const isGoogleDisplay =
    formData.platform === AdPlatform.GOOGLE && formData.objective === CampaignObjective.AWARENESS;

  // Google Ads references a YouTube-hosted video by URL rather than accepting
  // uploaded bytes, so Google video creatives swap the file uploader + cover
  // for a single YouTube-URL field.
  const isGoogleVideo =
    formData.platform === AdPlatform.GOOGLE && activeCreative?.type === CreativeType.VIDEO;

  // Meta and Google pool multiple images per ad (asset feeds / RDA) and mix
  // them with the text pools automatically. X and TikTok run one image per ad.
  const supportsImagePool =
    formData.platform === AdPlatform.META || formData.platform === AdPlatform.GOOGLE;
  const imagePoolMax = supportsImagePool ? (constraints?.images.max ?? 1) : 1;

  // Build UploadFileItem[] for the CreativeUploader from form state (per slot)
  const uploaderFiles = useMemo(
    () =>
      buildUploadFileItems(
        activeCreative?.mediaFiles ?? [],
        activeCreative?.mediaPreviews ?? [],
        uploadStates,
        uploadErrors,
      ),
    [activeCreative?.mediaFiles, activeCreative?.mediaPreviews, uploadStates, uploadErrors],
  );

  const squareUploaderFiles = useMemo(
    () =>
      buildUploadFileItems(
        activeCreative?.squareMediaFiles ?? [],
        activeCreative?.squareMediaPreviews ?? [],
        uploadStates,
        uploadErrors,
      ),
    [
      activeCreative?.squareMediaFiles,
      activeCreative?.squareMediaPreviews,
      uploadStates,
      uploadErrors,
    ],
  );

  // True while any manually-added file is still uploading (or parked waiting for
  // orgId). Used to block "Continue" so the user can't advance with no real image.
  // Video upload state for the active VIDEO creative (presigned-PUT flow,
  // separate from the base64 image uploadStates map).
  const [videoUploadState, setVideoUploadState] = useState<'idle' | 'uploading' | 'done' | 'error'>(
    'idle',
  );

  // Per-card image upload state for the active CAROUSEL creative, keyed by card index.
  const [cardUploading, setCardUploading] = useState<Record<number, boolean>>({});

  // Cover-image (thumbnail) upload state for the active VIDEO creative.
  const [thumbnailUploading, setThumbnailUploading] = useState(false);

  const isUploadingMedia =
    Object.values(uploadStates).some((s) => s === 'uploading') ||
    videoUploadState === 'uploading' ||
    thumbnailUploading ||
    Object.values(cardUploading).some(Boolean);

  const uploadCreativeImageMutation = useUploadCreativeImage();
  const uploadCreativeVideoMutation = useUploadCreativeVideo();
  const orgId = useAuthStore((s) => s.orgId);

  // UploadFileItem[] for the video slot, built from form state.
  const videoUploaderFiles = useMemo<UploadFileItem[]>(() => {
    const file = activeCreative?.videoFile;
    const preview = activeCreative?.videoPreview;
    if (!file && !preview) return [];
    const progress =
      videoUploadState === 'uploading' ? 35 : videoUploadState === 'error' ? -1 : 100;
    return [
      {
        id: 'video-0',
        file: file as File,
        previewUrl: preview ?? '',
        progress,
        error:
          videoUploadState === 'error'
            ? 'Upload failed — remove the video and add it again.'
            : undefined,
      },
    ];
  }, [activeCreative?.videoFile, activeCreative?.videoPreview, videoUploadState]);

  const handleVideoFilesChange = useCallback(
    (newFiles: UploadFileItem[]) => {
      const item = newFiles[0];
      const targetIndex = activeCreativeIndex;

      // Revoke the previous blob preview before overwriting/clearing it.
      const prevPreview = getValues(`creatives.${targetIndex}.videoPreview`);
      if (prevPreview?.startsWith('blob:') && prevPreview !== item?.previewUrl) {
        URL.revokeObjectURL(prevPreview);
      }

      // Cleared the slot — drop file, preview and any uploaded key.
      if (!item) {
        setValue(`creatives.${targetIndex}.videoFile`, undefined);
        setValue(`creatives.${targetIndex}.videoPreview`, undefined);
        setValue(`creatives.${targetIndex}.videoStorageKey`, undefined, {
          shouldDirty: true,
        });
        setVideoUploadState('idle');
        return;
      }

      setValue(`creatives.${targetIndex}.videoFile`, item.file);
      setValue(`creatives.${targetIndex}.videoPreview`, item.previewUrl);
      setValue(`creatives.${targetIndex}.videoStorageKey`, undefined);

      void (async () => {
        const constraintsForPlatform = formData.platform
          ? PLATFORM_FILE_CONSTRAINTS[formData.platform]
          : undefined;
        if (constraintsForPlatform) {
          const durationError = await validateVideoDuration(item.file, constraintsForPlatform);
          if (durationError) {
            addToast({ message: durationError, variant: 'error' });
            // Rejected — revoke the preview set above.
            if (item.previewUrl.startsWith('blob:')) {
              URL.revokeObjectURL(item.previewUrl);
            }
            setValue(`creatives.${targetIndex}.videoFile`, undefined);
            setValue(`creatives.${targetIndex}.videoPreview`, undefined);
            setVideoUploadState('idle');
            return;
          }
        }

        if (!orgId) {
          addToast({
            message: 'Still loading your organization — try again in a moment.',
            variant: 'error',
          });
          return;
        }

        setVideoUploadState('uploading');
        uploadCreativeVideoMutation.mutate(
          { file: item.file, organizationId: orgId },
          {
            onSuccess: ({ videoStorageKey }) => {
              setValue(`creatives.${targetIndex}.videoStorageKey`, videoStorageKey, {
                shouldDirty: true,
              });
              setVideoUploadState('done');
              setPlatformErrors([]);
            },
            onError: () => setVideoUploadState('error'),
          },
        );
      })();
    },
    [
      activeCreativeIndex,
      formData.platform,
      orgId,
      setValue,
      getValues,
      addToast,
      uploadCreativeVideoMutation,
    ],
  );

  // ── Carousel cards ──────────────────────────────────────────────────────
  const carouselCards = activeCreative?.carouselCards ?? [];

  const addCarouselCard = useCallback(() => {
    const idx = activeCreativeIndex;
    const cards = getValues(`creatives.${idx}.carouselCards`) ?? [];
    if (cards.length >= CAROUSEL_MAX_CARDS) return;
    setValue(`creatives.${idx}.carouselCards`, [...cards, {}], { shouldDirty: true });
  }, [activeCreativeIndex, getValues, setValue]);

  const removeCarouselCard = useCallback(
    (cardIndex: number) => {
      const idx = activeCreativeIndex;
      const cards = getValues(`creatives.${idx}.carouselCards`) ?? [];
      setValue(
        `creatives.${idx}.carouselCards`,
        cards.filter((_, i) => i !== cardIndex),
        { shouldDirty: true },
      );
    },
    [activeCreativeIndex, getValues, setValue],
  );

  const setCarouselCardField = useCallback(
    (cardIndex: number, field: 'headline' | 'destinationUrl', value: string) => {
      const idx = activeCreativeIndex;
      const cards = [...(getValues(`creatives.${idx}.carouselCards`) ?? [])];
      cards[cardIndex] = { ...cards[cardIndex], [field]: value };
      setValue(`creatives.${idx}.carouselCards`, cards, { shouldDirty: true });
    },
    [activeCreativeIndex, getValues, setValue],
  );

  const handleCardImageSelect = useCallback(
    (cardIndex: number, file: File | undefined) => {
      if (!file) return;
      if (!orgId) {
        addToast({
          message: 'Still loading your organization — try again in a moment.',
          variant: 'error',
        });
        return;
      }
      const targetIndex = activeCreativeIndex;
      setCardUploading((s) => ({ ...s, [cardIndex]: true }));
      uploadCreativeImageMutation.mutate(
        { file, organizationId: orgId },
        {
          onSuccess: (uploaded) => {
            const cards = [...(getValues(`creatives.${targetIndex}.carouselCards`) ?? [])];
            cards[cardIndex] = {
              ...cards[cardIndex],
              imageStorageKey: uploaded.imageStorageKey,
              imageUrl: uploaded.imageUrl,
            };
            setValue(`creatives.${targetIndex}.carouselCards`, cards, { shouldDirty: true });
            setCardUploading((s) => ({ ...s, [cardIndex]: false }));
            setPlatformErrors([]);
          },
          onError: () => setCardUploading((s) => ({ ...s, [cardIndex]: false })),
        },
      );
    },
    [activeCreativeIndex, orgId, getValues, setValue, addToast, uploadCreativeImageMutation],
  );

  const handleVideoThumbnailSelect = useCallback(
    (file: File | undefined) => {
      if (!file) return;
      if (!orgId) {
        addToast({
          message: 'Still loading your organization — try again in a moment.',
          variant: 'error',
        });
        return;
      }
      const targetIndex = activeCreativeIndex;
      setThumbnailUploading(true);
      uploadCreativeImageMutation.mutate(
        { file, organizationId: orgId },
        {
          onSuccess: (uploaded) => {
            setValue(
              `creatives.${targetIndex}.videoThumbnailStorageKey`,
              uploaded.imageStorageKey,
              { shouldDirty: true },
            );
            setValue(`creatives.${targetIndex}.videoThumbnailPreview`, uploaded.imageUrl);
            setThumbnailUploading(false);
            setPlatformErrors([]);
          },
          onError: () => setThumbnailUploading(false),
        },
      );
    },
    [activeCreativeIndex, orgId, setValue, addToast, uploadCreativeImageMutation],
  );

  const startUpload = useCallback(
    (file: File, targetIndex: number, slot: ImageSlot) => {
      const fp = fileFingerprint(file);
      setUploadStates((s) => ({ ...s, [fp]: 'uploading' }));
      // Re-adding a previously rejected file starts fresh.
      setUploadErrors((s) => {
        if (!(fp in s)) return s;
        const next = { ...s };
        delete next[fp];
        return next;
      });

      void (async () => {
        // Reject dimensions the platform would refuse at launch (e.g. Meta
        // dynamic creative rejects <600px or unsupported ratios with a cryptic
        // subcode-1885558 error after the whole wizard).
        const fileConstraints = formData.platform
          ? PLATFORM_FILE_CONSTRAINTS[formData.platform]
          : undefined;
        if (fileConstraints) {
          const dimensionError = await validateImageDimensions(file, fileConstraints);
          if (dimensionError) {
            // Reason is surfaced inline on the file item — toasts don't render
            // when the app is embedded via module federation.
            setUploadErrors((s) => ({ ...s, [fp]: dimensionError }));
            setUploadStates((s) => ({ ...s, [fp]: 'error' }));
            addToast({ message: dimensionError, variant: 'error' });
            return;
          }
        }

        // orgId can be momentarily null while the session hydrates. Don't drop the
        // upload silently — park it and fire once orgId arrives (queue-flush effect).
        if (!orgId) {
          orgIdQueueRef.current.push({ file, index: targetIndex, slot });
          return;
        }

        uploadCreativeImageMutation.mutate(
          { file, organizationId: orgId },
          {
            onSuccess: (uploaded) => {
              // Read the latest value (not a stale closure) and write to the index
              // + slot the file was added to. Literal paths keep RHF's FieldPath
              // typing happy. The fingerprint is persisted ON the entry so
              // uploader removal still finds it after remounts/draft reloads.
              const entry = { ...uploaded, sourceFileFingerprint: fp };
              if (slot === 'square') {
                const current = getValues(`creatives.${targetIndex}.squareGeneratedImages`) ?? [];
                setValue(`creatives.${targetIndex}.squareGeneratedImages`, [...current, entry], {
                  shouldDirty: true,
                });
              } else {
                const current = getValues(`creatives.${targetIndex}.generatedImages`) ?? [];
                setValue(`creatives.${targetIndex}.generatedImages`, [...current, entry], {
                  shouldDirty: true,
                });
              }
              setUploadStates((s) => ({ ...s, [fp]: 'done' }));
              // Clear any stale "add an image" error now that one actually landed;
              // the next Continue re-validates anything still wrong.
              setPlatformErrors([]);
            },
            onError: () => {
              setUploadStates((s) => ({ ...s, [fp]: 'error' }));
            },
          },
        );
      })();
    },
    [orgId, uploadCreativeImageMutation, getValues, setValue, formData.platform, addToast],
  );

  const handleSlotFilesChange = useCallback(
    (slot: ImageSlot, newFiles: UploadFileItem[]) => {
      const files = newFiles.map((item) => item.file);
      const previews = newFiles.map((item) => item.previewUrl);

      // Persist files/previews and drop the uploaded image of each REMOVED
      // file so a stale storage key can't reach the launch payload — while
      // keeping the rest of the pool (and AI-generated images, which have no
      // source file) intact. Matching runs on the entry's persisted
      // sourceFileFingerprint, so it survives remounts and draft reloads.
      // Literal field paths per slot keep RHF's typing happy.
      const currentFingerprints = new Set(files.filter(Boolean).map(fileFingerprint));
      const removedFingerprints = (prev: File[]) =>
        new Set(prev.map(fileFingerprint).filter((fp) => !currentFingerprints.has(fp)));
      const withoutRemoved = (images: WizardCreative['generatedImages'], removed: Set<string>) =>
        (images ?? []).filter(
          (img) => !img.sourceFileFingerprint || !removed.has(img.sourceFileFingerprint),
        );

      let prevFiles: File[];
      if (slot === 'square') {
        prevFiles = getValues(`creatives.${activeCreativeIndex}.squareMediaFiles`) ?? [];
        const removed = removedFingerprints(prevFiles);
        setValue(`creatives.${activeCreativeIndex}.squareMediaFiles`, files);
        setValue(`creatives.${activeCreativeIndex}.squareMediaPreviews`, previews);
        if (removed.size > 0) {
          const current = getValues(`creatives.${activeCreativeIndex}.squareGeneratedImages`) ?? [];
          setValue(
            `creatives.${activeCreativeIndex}.squareGeneratedImages`,
            withoutRemoved(current, removed),
            { shouldDirty: true },
          );
        }
      } else {
        prevFiles = getValues(`creatives.${activeCreativeIndex}.mediaFiles`) ?? [];
        const removed = removedFingerprints(prevFiles);
        setValue(`creatives.${activeCreativeIndex}.mediaFiles`, files);
        setValue(`creatives.${activeCreativeIndex}.mediaPreviews`, previews);
        if (removed.size > 0) {
          const current = getValues(`creatives.${activeCreativeIndex}.generatedImages`) ?? [];
          setValue(
            `creatives.${activeCreativeIndex}.generatedImages`,
            withoutRemoved(current, removed),
            { shouldDirty: true },
          );
        }
      }

      // Diff against the previous list to find newly added files. Identity is
      // (name, size, lastModified) since the same browser-picked File object
      // is preserved across re-renders by the uploader.
      const prevFingerprints = new Set(prevFiles.map(fileFingerprint));
      const newlyAdded = files.filter((f) => f && !prevFingerprints.has(fileFingerprint(f)));
      const targetIndex = activeCreativeIndex;
      for (const file of newlyAdded) {
        startUpload(file, targetIndex, slot);
      }
    },
    [activeCreativeIndex, setValue, getValues, startUpload],
  );

  const handleLandscapeFilesChange = useCallback(
    (newFiles: UploadFileItem[]) => handleSlotFilesChange('landscape', newFiles),
    [handleSlotFilesChange],
  );
  const handleSquareFilesChange = useCallback(
    (newFiles: UploadFileItem[]) => handleSlotFilesChange('square', newFiles),
    [handleSlotFilesChange],
  );

  // Fire any uploads parked before the session's orgId was ready.
  useEffect(() => {
    if (!orgId || orgIdQueueRef.current.length === 0) return;
    const queued = orgIdQueueRef.current;
    orgIdQueueRef.current = [];
    for (const { file, index, slot } of queued) {
      startUpload(file, index, slot);
    }
  }, [orgId, startUpload]);

  const handleRemoveGeneratedImage = useCallback(
    (imageIndex: number) => {
      const currentGenerated = watchedCreatives[activeCreativeIndex]?.generatedImages ?? [];
      setValue(
        `creatives.${activeCreativeIndex}.generatedImages`,
        currentGenerated.filter((_, index) => index !== imageIndex),
      );
    },
    [activeCreativeIndex, watchedCreatives, setValue],
  );

  const handleCopyLimitReached = useCallback(
    (message: string) => {
      addToast({ message, variant: 'warning' });
    },
    [addToast],
  );

  /**
   * Writes both copy pools independently — each is its own list, so unlike the
   * old paired cards there's no shared length to keep in sync. An empty pool
   * falls back to whatever is already in the form.
   */
  const applyCopyPools = useCallback(
    (nextHeadlines: string[], nextDescriptions: string[]) => {
      const headlines = nextHeadlines.length
        ? nextHeadlines
        : (getValues(`creatives.${activeCreativeIndex}.headlines`) ?? []);
      const descriptions = nextDescriptions.length
        ? nextDescriptions
        : (getValues(`creatives.${activeCreativeIndex}.descriptions`) ?? []);
      setValue(`creatives.${activeCreativeIndex}.headlines`, ensureAtLeastOne(headlines), {
        shouldDirty: true,
      });
      setValue(`creatives.${activeCreativeIndex}.descriptions`, ensureAtLeastOne(descriptions), {
        shouldDirty: true,
      });
    },
    [activeCreativeIndex, getValues, setValue],
  );

  const handleGenerateCopy = useCallback(async () => {
    if (!formData.name || !formData.platform) {
      addToast({
        message: 'Add campaign name and platform before generating copy',
        variant: 'warning',
      });
      return;
    }

    try {
      const result = await copySuggestion.mutateAsync({
        platform: formData.platform,
        objective: formData.objective,
        eventName: formData.eventName ?? formData.name,
        eventDescription: activeCreative?.descriptions?.[0],
        eventCategory: formData.objective,
        location: formData.eventVenue ?? formData.targetingConfig?.locations?.[0]?.name,
        destinationUrl,
        callToAction:
          CTA_OPTIONS.find((option) => option.value === activeCreative?.callToAction)?.label ??
          undefined,
        targetAudience: formData.targetingConfig?.interests?.map((interest) => interest.name),
        variations: 5,
      });

      const payload = result.payload as {
        variations?: {
          id: number;
          headline: string;
          body: string;
          callToAction: string;
          headlineCharCount: number;
          bodyCharCount: number;
        }[];
        characterLimits?: { headline: number; body: number };
      };

      if (payload.variations && payload.variations.length > 0) {
        // Auto-fill the form with every generated option, capped by platform limits.
        const maxHeadlines = constraints?.headlines.max ?? payload.variations.length;
        const maxDescriptions = constraints?.descriptions.max ?? payload.variations.length;
        const headlines = [
          ...new Set(payload.variations.map((v) => v.headline).filter(Boolean)),
        ].slice(0, maxHeadlines);
        const descriptions = [
          ...new Set(payload.variations.map((v) => v.body).filter(Boolean)),
        ].slice(0, maxDescriptions);

        applyCopyPools(headlines, descriptions);
        const cta = normalizeCallToAction(payload.variations.map((v) => v.callToAction));
        if (cta) {
          setValue(`creatives.${activeCreativeIndex}.callToAction`, cta);
        }
        // Count real copy, not the padding the variation cards need.
        const appliedHeadlines = headlines.filter(Boolean).length;
        const appliedDescriptions = descriptions.filter(Boolean).length;
        addToast({
          message: `Applied ${appliedHeadlines} headline${appliedHeadlines === 1 ? '' : 's'} and ${appliedDescriptions} description${appliedDescriptions === 1 ? '' : 's'}`,
          variant: 'success',
        });
      } else {
        // Fallback: auto-apply if no structured variations
        const headlines = normalizeStringArray(
          (result.payload as Record<string, unknown>).headlines,
        );
        const descriptions = normalizeStringArray(
          (result.payload as Record<string, unknown>).descriptions,
        );
        const callToAction = normalizeCallToAction(
          (result.payload as Record<string, unknown>).callToActionSuggestions,
        );

        applyCopyPools(headlines, descriptions);
        if (callToAction) {
          setValue(`creatives.${activeCreativeIndex}.callToAction`, callToAction);
        }
        addToast({ message: 'AI copy applied to the creative', variant: 'success' });
      }
    } catch {
      addToast({ message: 'Failed to generate AI copy', variant: 'error' });
    }
  }, [
    activeCreative?.callToAction,
    activeCreative?.descriptions,
    activeCreativeIndex,
    addToast,
    applyCopyPools,
    constraints,
    copySuggestion,
    destinationUrl,
    formData.eventName,
    formData.eventVenue,
    formData.name,
    formData.objective,
    formData.platform,
    formData.targetingConfig,
    setValue,
  ]);

  const handleRefreshImageUrl = useCallback(
    async (imageIndex: number) => {
      const currentGenerated = watchedCreatives[activeCreativeIndex]?.generatedImages ?? [];
      const image = currentGenerated[imageIndex];
      if (!image?.imageStorageKey) return;

      try {
        const { url, expiresAt } = await fetchAssetAccessUrl(image.imageStorageKey);
        // Never clobber a good creative with an empty refresh. `imageStorageKey`
        // is what actually launches; an `<img>` onError that resolves to no URL
        // used to wipe `imageUrl`/`expiresAt` to undefined, which then failed
        // schema validation and silently blocked the wizard. If the refresh
        // comes back without a usable URL, keep the existing values.
        if (!url) return;
        const updated = [...currentGenerated];
        updated[imageIndex] = { ...image, imageUrl: url, expiresAt: expiresAt ?? image.expiresAt };
        setValue(`creatives.${activeCreativeIndex}.generatedImages`, updated);
      } catch {
        addToast({ message: 'Failed to refresh image URL', variant: 'error' });
      }
    },
    [activeCreativeIndex, watchedCreatives, setValue, addToast],
  );

  const validatePlatformConstraints = useCallback(
    (data: CreativeFormValues): string[] => {
      if (!constraints) return [];

      const validationErrors: string[] = [];

      for (let i = 0; i < data.creatives.length; i++) {
        const creative = data.creatives[i];
        if (!creative) continue;
        const prefix = data.creatives.length > 1 ? `Creative ${i + 1}: ` : '';

        const headlineError = validateFieldCount(
          'headline',
          creative.headlines,
          constraints.headlines,
        );
        if (headlineError) validationErrors.push(`${prefix}${headlineError}`);

        const descError = validateFieldCount(
          'description',
          creative.descriptions,
          constraints.descriptions,
        );
        if (descError) validationErrors.push(`${prefix}${descError}`);

        // Check character limits. Labels follow the UI: headlines and
        // descriptions are numbered independently within their own list.
        const label = (idx: number, kind: 'headline' | 'description') =>
          `${kind === 'headline' ? 'Headline' : 'Description'} ${idx + 1}`;

        creative.headlines.forEach((headline, idx) => {
          if (headline.length > constraints.headlines.maxLength) {
            validationErrors.push(
              `${prefix}${label(idx, 'headline')} exceeds ${constraints.headlines.maxLength} character limit.`,
            );
          }
        });

        creative.descriptions.forEach((desc, idx) => {
          if (desc.length > constraints.descriptions.maxLength) {
            validationErrors.push(
              `${prefix}${label(idx, 'description')} exceeds ${constraints.descriptions.maxLength} character limit.`,
            );
          }
        });
      }

      validationErrors.push(
        ...validateCreativeMedia(data.creatives, formData.platform, formData.objective),
      );

      setPlatformErrors(validationErrors);
      return validationErrors;
    },
    [constraints, formData.platform, formData.objective],
  );

  const onSubmit = (data: CreativeFormValues) => {
    if (isBoosting) {
      // Nothing about the creative form applies: the published post carries its
      // own copy and media, and Meta resolves it by id at launch.
      updateFormData({ boostPostId: data.boostPostId });
      markStepComplete('creative');
      onNext();
      return;
    }

    const platformErrs = validatePlatformConstraints(data);
    if (platformErrs.length > 0) {
      addToast({
        message: platformErrs[0] ?? 'Please fix the highlighted errors before continuing.',
        variant: 'error',
      });
      scrollToFirstError();
      return;
    }

    updateFormData({
      ...data,
      boostPostId: undefined,
      creatives: data.creatives.map((creative) => ({
        ...creative,
        mediaFiles: creative.mediaFiles ?? [],
        mediaPreviews: creative.mediaPreviews ?? [],
        generatedImages: creative.generatedImages ?? [],
      })),
    });
    markStepComplete('creative');
    onNext();
  };

  return (
    <form
      className={styles.step}
      onSubmit={handleSubmit(onSubmit, onInvalidSubmit)}
      onKeyDown={(e) => {
        // Only the Continue button should submit the form; Enter in any
        // field must not advance the wizard on its own.
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'BUTTON') {
          e.preventDefault();
        }
      }}
      noValidate
    >
      {/* Platform validation errors */}
      {platformErrors.length > 0 && (
        <div className={styles.platformErrors} role="alert">
          {platformErrors.map((error) => (
            <p key={error} className={styles.platformErrorItem}>
              {error}
            </p>
          ))}
        </div>
      )}

      {canBoost && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Ad Content</h2>
          <div className={styles.typeGrid}>
            <button
              type="button"
              className={clsx(styles.typeCard, !isBoosting && styles.typeCardSelected)}
              onClick={() => setIsBoosting(false)}
              aria-pressed={!isBoosting}
            >
              <Image size={20} />
              <span className={styles.typeLabel}>Build new creative</span>
              <span className={styles.typeDescription}>
                Write copy and upload images for a brand new ad.
              </span>
            </button>
            <button
              type="button"
              className={clsx(styles.typeCard, isBoosting && styles.typeCardSelected)}
              onClick={() => setIsBoosting(true)}
              aria-pressed={isBoosting}
            >
              <Megaphone size={20} />
              <span className={styles.typeLabel}>Boost an existing Page post</span>
              <span className={styles.typeDescription}>
                Promote a post you already published. Likes, comments and shares stay on it.
              </span>
            </button>
          </div>
        </div>
      )}

      {isBoosting && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Post to Boost</h2>
          <Controller
            name="boostPostId"
            control={control}
            render={({ field }) => (
              <MetaPostPicker
                adAccountId={formData.adAccountId ?? ''}
                pageId={formData.pageId}
                value={field.value || undefined}
                onChange={field.onChange}
                error={errors.boostPostId?.message}
              />
            )}
          />
        </div>
      )}

      {!isBoosting && (
        <>
          {/* Creative Type */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Creative Type</h2>
            <Controller
              name={`creatives.${activeCreativeIndex}.type`}
              control={control}
              render={({ field }) => (
                <div className={styles.typeGrid}>
                  {CREATIVE_TYPE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      data-testid={`creative-type-${opt.value}`}
                      className={clsx(
                        styles.typeCard,
                        field.value === opt.value && styles.typeCardSelected,
                      )}
                      onClick={() => field.onChange(opt.value)}
                      aria-pressed={field.value === opt.value}
                    >
                      <Image size={20} />
                      <span className={styles.typeLabel}>{opt.label}</span>
                      <span className={styles.typeDescription}>{opt.description}</span>
                    </button>
                  ))}
                </div>
              )}
            />
          </div>

          {/* Platform help text */}
          {platformHelpText && (
            <div className={styles.platformHelp}>
              <AlertCircle size={16} />
              <span>{platformHelpText}</span>
            </div>
          )}

          {/* Ad copy — headlines and descriptions are two independent pools */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Ad Copy</h2>
            <div className={styles.mixNotice}>
              <Shuffle size={16} />
              <span>
                Headlines and descriptions are mixed automatically by the platform — they
                aren&apos;t paired, so manage each list on its own.
              </span>
            </div>
            {generateCopyEnabled && (
              <div className={styles.sectionActions}>
                <AppButton
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => void handleGenerateCopy()}
                  loading={copySuggestion.isPending}
                >
                  <Sparkles size={14} />
                  Generate Copy with AI
                </AppButton>
              </div>
            )}

            <AdCopyFields
              form={form}
              creativeIndex={activeCreativeIndex}
              constraints={constraints}
              headlines={activeCreative?.headlines ?? ['']}
              descriptions={activeCreative?.descriptions ?? ['']}
              onLimitReached={handleCopyLimitReached}
            />
          </div>

          {/* Media Upload */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Media</h2>
            <p className={styles.sectionDescription}>
              Upload images or videos for your ad creative.
            </p>

            <div className={styles.mediaArea}>
              {activeCreative?.type === CreativeType.VIDEO && isGoogleVideo ? (
                <>
                  {/* Google references a public YouTube video by URL — no byte upload. */}
                  <p className={styles.sectionDescription}>
                    Paste the YouTube URL of your video (it must be public on YouTube)
                  </p>
                  <AppInput
                    placeholder="https://www.youtube.com/watch?v=…"
                    data-testid="creative-youtube-url-input"
                    {...register(`creatives.${activeCreativeIndex}.youtubeVideoUrl`)}
                    error={errors.creatives?.[activeCreativeIndex]?.youtubeVideoUrl?.message}
                  />
                </>
              ) : activeCreative?.type === CreativeType.VIDEO ? (
                <>
                  {/* Video creatives upload straight to S3 (presigned PUT). */}
                  <p className={styles.sectionDescription}>Upload a video (MP4, MOV, or WebM)</p>
                  <CreativeUploader
                    files={videoUploaderFiles}
                    onFilesChange={handleVideoFilesChange}
                    platform={formData.platform}
                    multiple={false}
                    maxFiles={1}
                    inputTestId="creative-video-input"
                  />
                  {/* Meta requires a thumbnail (cover image) for video ads. */}
                  <p className={styles.sectionDescription}>Cover image (required)</p>
                  <div className={styles.videoThumbnail}>
                    <div className={styles.videoThumbnailImage}>
                      {activeCreative?.videoThumbnailPreview ? (
                        <img src={activeCreative.videoThumbnailPreview} alt="Video cover" />
                      ) : (
                        <div className={styles.videoThumbnailPlaceholder}>
                          <Image size={20} />
                        </div>
                      )}
                      <label className={styles.videoThumbnailUpload}>
                        {thumbnailUploading
                          ? 'Uploading…'
                          : activeCreative?.videoThumbnailStorageKey
                            ? 'Replace cover'
                            : 'Upload cover image'}
                        <input
                          type="file"
                          data-testid="creative-video-cover-input"
                          accept="image/png,image/jpeg,image/gif,image/webp"
                          hidden
                          onChange={(e) =>
                            handleVideoThumbnailSelect(e.target.files?.[0] ?? undefined)
                          }
                        />
                      </label>
                    </div>
                  </div>
                </>
              ) : activeCreative?.type === CreativeType.CAROUSEL ? (
                <div className={styles.carouselCards}>
                  <p className={styles.sectionDescription}>
                    Add 2-{CAROUSEL_MAX_CARDS} cards — each with its own image, headline and link.
                  </p>
                  {carouselCards.map((card, cardIndex) => (
                    <div key={cardIndex} className={styles.carouselCard}>
                      <div className={styles.carouselCardImage}>
                        {card.imageUrl ? (
                          <img src={card.imageUrl} alt={`Card ${cardIndex + 1}`} />
                        ) : (
                          <div className={styles.carouselCardPlaceholder}>
                            <Image size={20} />
                          </div>
                        )}
                        <label className={styles.carouselCardUpload}>
                          {cardUploading[cardIndex]
                            ? 'Uploading…'
                            : card.imageStorageKey
                              ? 'Replace'
                              : 'Upload image'}
                          <input
                            type="file"
                            data-testid={`creative-carousel-card-input-${cardIndex}`}
                            accept="image/png,image/jpeg,image/gif,image/webp"
                            hidden
                            onChange={(e) =>
                              handleCardImageSelect(cardIndex, e.target.files?.[0] ?? undefined)
                            }
                          />
                        </label>
                      </div>
                      <div className={styles.carouselCardFields}>
                        <AppInput
                          label={`Card ${cardIndex + 1} headline`}
                          value={card.headline ?? ''}
                          maxLength={90}
                          onChange={(e) =>
                            setCarouselCardField(cardIndex, 'headline', e.target.value)
                          }
                        />
                        <AppInput
                          label="Card link (optional)"
                          placeholder={destinationUrl || 'https://…'}
                          value={card.destinationUrl ?? ''}
                          onChange={(e) =>
                            setCarouselCardField(cardIndex, 'destinationUrl', e.target.value)
                          }
                        />
                      </div>
                      <button
                        type="button"
                        className={styles.carouselCardRemove}
                        onClick={() => removeCarouselCard(cardIndex)}
                        aria-label={`Remove card ${cardIndex + 1}`}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                  {carouselCards.length < CAROUSEL_MAX_CARDS && (
                    <AppButton
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={addCarouselCard}
                    >
                      <Plus size={14} />
                      Add card
                    </AppButton>
                  )}
                </div>
              ) : isGoogleDisplay ? (
                <>
                  {/* Google Display needs >=1 landscape (1.91:1) AND >=1 square (1:1)
                  image; both slots pool up to 15 and Google mixes them freely. */}
                  <p className={styles.sectionDescription}>
                    Landscape images (1.91:1) — up to {imagePoolMax}
                  </p>
                  <CreativeUploader
                    files={uploaderFiles}
                    onFilesChange={handleLandscapeFilesChange}
                    platform={formData.platform}
                    multiple
                    maxFiles={imagePoolMax}
                    inputTestId="creative-image-input"
                  />
                  <p className={styles.sectionDescription}>
                    Square images (1:1) — up to {imagePoolMax}
                  </p>
                  <CreativeUploader
                    files={squareUploaderFiles}
                    onFilesChange={handleSquareFilesChange}
                    platform={formData.platform}
                    multiple
                    maxFiles={imagePoolMax}
                    inputTestId="creative-square-input"
                  />
                </>
              ) : (
                // Image creative (IMAGE/TEXT) — carousel & video are handled above.
                // Meta/Google accept an image pool; X/TikTok take a single image.
                <CreativeUploader
                  files={uploaderFiles}
                  onFilesChange={handleLandscapeFilesChange}
                  platform={formData.platform}
                  multiple={supportsImagePool}
                  maxFiles={imagePoolMax}
                  inputTestId="creative-image-input"
                />
              )}

              {/* AI-generated images (separate from file uploads) — single-image only */}
              {activeCreative?.type !== CreativeType.VIDEO &&
                activeCreative?.type !== CreativeType.CAROUSEL &&
                generatedPreviewItems.length > 0 && (
                  <div className={styles.mediaPreviews}>
                    {generatedPreviewItems.map((previewItem) => {
                      const generatedImage = (activeCreative?.generatedImages ?? [])[
                        previewItem.index
                      ];
                      const isExpired = generatedImage?.expiresAt
                        ? new Date(generatedImage.expiresAt).getTime() < Date.now()
                        : false;

                      return (
                        <div key={previewItem.key} className={styles.mediaPreview}>
                          {isExpired ? (
                            <button
                              type="button"
                              className={styles.expiredOverlay}
                              onClick={() => void handleRefreshImageUrl(previewItem.index)}
                            >
                              <span className={styles.expiredText}>Expired - Click to refresh</span>
                            </button>
                          ) : (
                            <img
                              src={previewItem.url}
                              alt="AI-generated creative preview"
                              className={styles.previewImage}
                              onError={() => {
                                void handleRefreshImageUrl(previewItem.index);
                              }}
                            />
                          )}
                          <button
                            type="button"
                            className={styles.mediaRemove}
                            onClick={() => handleRemoveGeneratedImage(previewItem.index)}
                            aria-label="Remove generated image"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
            </div>
          </div>

          {/* Call to Action */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Call to Action</h2>
            <Controller
              name={`creatives.${activeCreativeIndex}.callToAction`}
              control={control}
              render={({ field }) => (
                <AppSelect
                  label="Button text"
                  options={CTA_OPTIONS}
                  value={field.value}
                  onValueChange={field.onChange}
                  error={errors.creatives?.[activeCreativeIndex]?.callToAction?.message}
                />
              )}
            />
          </div>

          {/* Destination URL */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Destination URL</h2>
            <div className={styles.urlInput}>
              <ExternalLink size={16} className={styles.urlIcon} />
              <AppInput
                placeholder="https://events.com/your-event"
                error={errors.destinationUrl?.message}
                {...register('destinationUrl')}
              />
            </div>
          </div>
        </>
      )}

      <div className={styles.actions}>
        <AppButton type="button" variant="ghost" onClick={onSaveDraft} loading={isSaving}>
          Save as Draft
        </AppButton>
        <div className={styles.navActions}>
          <AppButton type="button" variant="secondary" onClick={onBack}>
            Back
          </AppButton>
          <AppButton type="submit" loading={isUploadingMedia}>
            {isUploadingMedia ? 'Uploading…' : 'Continue'}
          </AppButton>
        </div>
      </div>
    </form>
  );
}

function normalizeStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter(Boolean);
}

function normalizeCallToAction(value: unknown): WizardCreative['callToAction'] | null {
  const suggestions = normalizeStringArray(value);
  const first = suggestions[0];

  if (!first) {
    return null;
  }

  const matched = CTA_OPTIONS.find((option) => option.label.toLowerCase() === first.toLowerCase());

  return matched?.value ?? null;
}
